import { PlusOutlined } from '@ant-design/icons';
import { Button, Card, Empty, Input, Modal, Space, Table, Typography, message } from 'antd';
import axios from 'axios';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { MdOutlineStickyNote2 } from 'react-icons/md';

import ProductImage from '../../components/productImage';
import rolesDictionary from '../../config/rolesDictionary';
import { isHandledByInterceptor } from '../../utils/apiError';
import './detail.less';

const { Text } = Typography;
const { TextArea } = Input;

/** Барааны зурагны хэмжээ */
const IMAGE_SIZE = 36;

/** roleCode-оор ролийн нэрийг олно */
const getRoleName = (roleCode) =>
  rolesDictionary.rolesDictionary.find((role) => String(role.roleCode) === String(roleCode))
    ?.name ?? '-';

const Notes = ({ notes = {}, getDetailData }) => {
  const [comment, setComment] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sending, setSending] = useState(false);

  /**
   * Захиалгын өөрийн тэмдэглэл + дэд захиалга (бараа) бүрийн тэмдэглэлийг
   * нэг жагсаалт болгож, шинээс хуучин рүү эрэмбэлнэ.
   */
  const allNotes = useMemo(() => {
    const orderNotes = notes?.orderNote ?? [];
    const childNotes = (notes?.children ?? []).flatMap((child) =>
      (child?.orderNote ?? []).map((note) => ({
        ...note,
        product: child.productImage,
        isProductNote: true,
      }))
    );

    return [...orderNotes, ...childNotes].sort(
      (a, b) => moment(b?.updatedAt).valueOf() - moment(a?.updatedAt).valueOf()
    );
  }, [notes]);

  const closeModal = () => {
    setIsModalOpen(false);
    setComment('');
  };

  const sendComment = async () => {
    const note = comment.trim();
    if (!note) return;

    setSending(true);
    try {
      const { data } = await axios.post('/api/v1/request/addNote', {
        code: notes?.code,
        note,
        noteType: 'normal',
      });

      // Алдааны кодтой хариуг interceptor мэдэгдсэн — амжилт гэж харуулахгүй
      if (data && !isHandledByInterceptor(data)) {
        message.success('Тэмдэглэл нэмэгдлээ.');
        closeModal();
        getDetailData?.();
      }
    } catch (error) {
      // Алдааны мэдэгдлийг axios interceptor аль хэдийн гаргасан — давхардуулахгүй
      console.error('addNote: ', error);
    } finally {
      setSending(false);
    }
  };

  const columns = [
    {
      key: 'index',
      title: '№',
      width: 20,
      align: 'center',
      render: (_, record, index) => <Text>{index + 1}</Text>,
    },
    {
      key: 'type',
      title: 'Төрөл',
      width: 200,
      render: (_, record) =>
        record.isProductNote ? (
          <ProductImage
            src={record.product}
            alt="Барааны зураг"
            size={IMAGE_SIZE}
            style={{ objectFit: 'cover', borderRadius: 4 }}
          />
        ) : (
          <>Захиалга</>
        ),
    },
    {
      key: 'date',
      title: 'Огноо',
      width: 200,
      render: (_, record) =>
        record?.updatedAt ? moment(record.updatedAt).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      key: 'roleCode',
      title: 'Роль',
      width: 200,
      render: (_, record) => getRoleName(record?.roleCode) || '-',
    },
    {
      key: 'author',
      title: 'Бичсэн',
      width: 200,
      render: (_, record) => record?.userName || '-',
    },
    {
      key: 'note',
      title: 'Тэмдэглэл',
      width: 200,
      render: (_, record) => record?.note || <Text type="secondary">-</Text>,
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%', padding: 16 }}>
      <Card
        size="middle"
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <MdOutlineStickyNote2 style={{ display: 'block', fontSize: '1em' }} />
            Тэмдэглэл
            {allNotes.length > 0 && <Text type="secondary">({allNotes.length})</Text>}
          </span>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)}>
            Тэмдэглэл нэмэх
          </Button>
        }
        styles={{ body: { padding: allNotes.length > 0 ? 0 : undefined } }}
      >
        <div style={{ padding: '10px' }}>
          {allNotes.length > 0 ? (
            <Table
              className="notes__table"
              size="large"
              columns={columns}
              dataSource={allNotes}
              rowKey={(record, index) => record?._id ?? index}
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Тэмдэглэл байхгүй байна."
              style={{ margin: 0 }}
            />
          )}
        </div>
      </Card>

      <Modal
        title="Тэмдэглэл нэмэх"
        open={isModalOpen}
        onOk={sendComment}
        onCancel={closeModal}
        okText="Нэмэх"
        cancelText="Болих"
        okButtonProps={{ disabled: !comment.trim(), loading: sending }}
        destroyOnClose
      >
        <TextArea
          autoFocus
          rows={4}
          placeholder="Тэмдэглэлээ бичнэ үү."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </Modal>
    </Space>
  );
};

export default Notes;
